'use client';
import { useEffect } from 'react';
 import AppImage from'@/components/ui/AppImage';
 import Icon from'@/components/ui/AppIcon';

interface Photo {
  id: string
  image: string
  alt: string
  title: string
  category: string
  date: string
}

interface LightboxProps {
  photos: Photo[]
  currentIndex: number
  onClose: () => void
  onNext: () => void
  onPrev: () => void
}

export default function Lightbox({
  photos,
  currentIndex,
  onClose,
  onNext,
  onPrev,
}: LightboxProps) {
  const currentPhoto = photos[currentIndex]

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') onNext()
      if (e.key === 'ArrowLeft') onPrev()
    }

    window.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [onClose, onNext, onPrev])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="fixed top-6 right-6 w-12 h-12 rounded-full glass-panel flex items-center justify-center text-white hover:bg-white/20 transition-colors z-50"
        aria-label="Close lightbox"
      >
        <Icon name="XMarkIcon" size={24} />
      </button>

      {/* Previous Button */}
      <button
        onClick={onPrev}
        className="fixed left-6 w-12 h-12 rounded-full glass-panel flex items-center justify-center text-white hover:bg-white/20 transition-colors z-50"
        aria-label="Previous photo"
        disabled={currentIndex === 0}
      >
        <Icon name="ChevronLeftIcon" size={24} />
      </button>

      {/* Next Button */}
      <button
        onClick={onNext}
        className="fixed right-6 w-12 h-12 rounded-full glass-panel flex items-center justify-center text-white hover:bg-white/20 transition-colors z-50"
        aria-label="Next photo"
        disabled={currentIndex === photos.length - 1}
      >
        <Icon name="ChevronRightIcon" size={24} />
      </button>

      {/* Image Container */}
      <div className="relative max-w-7xl max-h-[90vh] mx-auto px-6">
        <div className="relative">
          <AppImage
            src={currentPhoto.image}
            alt={currentPhoto.alt}
            className="max-h-[80vh] w-auto object-contain rounded-lg"
          />
        </div>

        {/* Photo Info */}
        <div className="mt-6 text-center space-y-2">
          <h3 className="text-2xl font-bold text-white">{currentPhoto.title}</h3>
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <span className="uppercase tracking-wider">{currentPhoto.category}</span>
            <span>·</span>
            <span>{currentPhoto.date}</span>
            <span>·</span>
            <span>
              {currentIndex + 1} / {photos.length}
            </span>
          </div>
        </div>
      </div>

      {/* Backdrop Click to Close */}
      <button
        onClick={onClose}
        className="absolute inset-0 -z-10"
        aria-label="Close lightbox"
      />
    </div>
  )
}